import { CallEffect, ForkEffect, GetContextEffect, JoinEffect, PutEffect, SelectEffect } from 'redux-saga/effects';
import { getContext, call, put, takeLatest, fork, join, SagaGenerator, retry, select } from 'typed-redux-saga';
import { Optional } from '../../../../../lib/types/Optional';
import { RemindersListResponse } from '../../../reminder/interfaces/Reminder.types';
import {
  MachineDetails,
  MachineDetailsVariantDatum,
  MachineUpdateResponse,
  GetMachineOperatingTimeForPeriod,
  MachineDetailsLatestCtrDatum,
  MachineDetailsTelemetriesDatum,
  MachineDetailsLatestRoutineDatum,
} from '../../interfaces/Machine.types';
import { MachineModalsActions } from '../../modals/state/machineModalsSlice';
import {
  IEditMachineInfoModalIsLoadingAction,
  IEditMachineInfoModalIsNotLoadingAction,
  IEditMachineNameModalIsLoadingAction,
  IEditMachineNameModalIsNotLoadingAction,
  IHideEditMachineInfoModalAction,
  IHideEditMachineNameModalAction,
} from '../../modals/state/machineModalsActions.types';
import {
  MachineAttachmentDeleteResponse,
  MachineAttachmentDownloadResponse,
  MachineAttachmentGetUploadLinkResponse,
  MachineAttachmentsListResponse,
} from '../../interfaces/MachineAttachment.types';
import {
  MACHINE_ATTACHMENT_LIST_DEFAULT_SORT_FIELD,
  MACHINE_ATTACHMENT_LIST_PAGE_SIZE_LARGE,
} from '../../utils/constants';
import type { MachinePositionHistoriesListResponse } from '../../interfaces/MachinePosition.types';
import {
  mapMachinePositionHistoryToEquipmentPositionSummary,
  sortPositionSummariesByEnteredAtDesc,
} from '../../utils/positionHistoryUtils';
import { FormDataBuilder } from '../../utils/FormDataBuilder';
import { MachineDetailsActions } from '../../machine-details/state/machineDetailsActions';
import {
  IPollGetMachineListAsReportRequestAction,
  MachineListActions,
} from '../../machine-list/state/machineListActions';
import {
  IGetMachineDetailsErrorAction,
  IGetMachineDetailsPictureSuccessAction,
  IGetMachineDetailsRequestAction,
  IGetMachineDetailsSuccessAction,
  IGetMachineOperatingTimeErrorAction,
  IGetMachineOperatingTimeRequestAction,
  IGetMachineOperatingTimeSuccessAction,
  IGetMachineNotificationListErrorAction,
  IGetMachineNotificationListRequestAction,
  IGetMachineNotificationListSuccessAction,
  IUpdateMachineErrorAction,
  IUpdateMachineRequestAction,
  IUpdateMachineSuccessAction,
  MachineDetailsPanelActions,
  IGetMachineRemindersRequestAction,
  IGetMachineRemindersErrorAction,
  IGetMachineRemindersSuccessAction,
  IGetMachineNotesErrorAction,
  IGetMachineNotesRequestAction,
  IGetMachineNotesSuccessAction,
  IGetMachinePositionHistoriesErrorAction,
  IGetMachinePositionHistoriesMoreDataErrorAction,
  IGetMachinePositionHistoriesMoreDataRequestAction,
  IGetMachinePositionHistoriesMoreDataSuccessAction,
  IGetMachinePositionHistoriesRequestAction,
  IGetMachinePositionHistoriesSuccessAction,
  IGetMachinePositionHistoriesSuccessOptions,
  IGetMachineAttachmentsSuccessAction,
  IGetMachineAttachmentsErrorAction,
  IGetMachineAttachmentsRequestAction,
  IDeleteMachineAttachmentRequestAction,
  IDeleteMachineAttachmentSuccessAction,
  IDeleteMachineAttachmentErrorAction,
  IAddMachineAttachmentErrorAction,
  IAddMachineAttachmentRequestAction,
  IAddMachineAttachmentSuccessAction,
  IDownloadMachineAttachmentErrorAction,
  IDownloadMachineAttachmentRequestAction,
  IDownloadMachineAttachmentSuccessAction,
  IPollMachineAttachmentsRequestAction,
  IMachineAttachmentsIsLoadingAction,
  IPollMachineAttachmentsOperation,
  IUpdateMachineInfoSuccessAction,
  IUpdateMachineInfoErrorAction,
  IUpdateMachineInfoRequestAction,
  IGetMachineDetailsLatestCtrSuccessAction,
  IGetMachineDetailsTelemetriesSuccessAction,
  IGetMachineDetailsLatestRoutineSuccessAction,
} from './machineDetailsPanelActions';
import { selectAttachmentsTotalCount } from './machineDetailsPanelSelectors';
import { IDependencies } from 'app/cross-cutting-concerns/dependency-injection/interfaces/IDependencies';
import { NotificationList } from 'app/modules/notification/interfaces/Notification.types';
import { NotesListResponse } from 'app/modules/note/interfaces/Note.types';
import { SortOrders } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { downloadLink } from 'app/utils/download-link/downloadLink';
import { POLL_INTERVAL, POLL_MAX_RETRIES } from 'config/constants';

const mapMachinePositionHistoriesResponse = (
  response: MachinePositionHistoriesListResponse
): IGetMachinePositionHistoriesSuccessOptions => {
  const positionHistories = response.machinePositionHistories;

  return {
    data: sortPositionSummariesByEnteredAtDesc(
      (positionHistories?.data ?? []).map(mapMachinePositionHistoryToEquipmentPositionSummary)
    ),
    paginationToken: positionHistories?.metadata?.paginationToken,
    totalCount: positionHistories?.metadata?.totalCount,
  };
};

export function* getMachineDetailsSaga(
  action: IGetMachineDetailsRequestAction
): Generator<
  | GetContextEffect
  | ForkEffect<MachineDetails>
  | ForkEffect<MachineDetailsVariantDatum>
  | ForkEffect<MachineDetailsTelemetriesDatum>
  | ForkEffect<MachineDetailsLatestCtrDatum>
  | ForkEffect<MachineDetailsLatestRoutineDatum>
  | JoinEffect
  | PutEffect<IGetMachineDetailsSuccessAction>
  | PutEffect<IGetMachineDetailsPictureSuccessAction>
  | PutEffect<IGetMachineDetailsTelemetriesSuccessAction>
  | PutEffect<IGetMachineDetailsLatestCtrSuccessAction>
  | PutEffect<IGetMachineDetailsLatestRoutineSuccessAction>
  | PutEffect<IGetMachineDetailsErrorAction>,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    const machineResponseTask = yield* fork(machineService.get, action.payload);
    const machinePictureResponseTask = yield* fork(machineService.getPicture, action.payload);
    const machineTelemetriesResponseTask = yield* fork(machineService.getTelemetries, action.payload);
    const machineLatestCtrResponseTask = yield* fork(machineService.getLatestCtr, action.payload);
    const machineLatestRoutineResponseTask = yield* fork(machineService.getLatestRoutine, action.payload);

    const machineResponse = yield* join(machineResponseTask);
    yield* put(MachineDetailsPanelActions.getMachineDetailsSuccess(machineResponse));

    const machinePictureResponse = yield* join(machinePictureResponseTask);
    yield* put(MachineDetailsPanelActions.getMachineDetailsPicturesSuccess(machinePictureResponse));

    const machineTelemetriesResponse = yield* join(machineTelemetriesResponseTask);
    yield* put(MachineDetailsActions.getMachineDetailsTelemetriesSuccess(machineTelemetriesResponse));

    const machineLatestCtrResponse = yield* join(machineLatestCtrResponseTask);
    yield* put(MachineDetailsActions.getMachineDetailsLatestCtrSuccess(machineLatestCtrResponse));

    const machineLatestRoutineResponse = yield* join(machineLatestRoutineResponseTask);
    yield* put(MachineDetailsPanelActions.getMachineDetailsLatestRoutineSuccess(machineLatestRoutineResponse));
  } catch (error) {
    yield* put(
      MachineDetailsPanelActions.getMachineDetailsError({
        error,
      })
    );
  }
}

export function* getMachineDetailsMetadataSaga(
  action: IGetMachineDetailsRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<MachineDetails>
  | PutEffect<IGetMachineDetailsSuccessAction>
  | PutEffect<IGetMachineDetailsErrorAction>,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    const machineMetadataResponse = yield* call(machineService.getMetadata, action.payload);
    yield* put(MachineDetailsPanelActions.getMachineDetailsMetadataSuccess(machineMetadataResponse));
  } catch (error) {
    yield* put(
      MachineDetailsPanelActions.getMachineDetailsMetadataError({
        error,
      })
    );
  }
}

export function* getMachineOperatingTimeSaga(
  action: IGetMachineOperatingTimeRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<GetMachineOperatingTimeForPeriod>
  | PutEffect<IGetMachineOperatingTimeSuccessAction>
  | PutEffect<IGetMachineOperatingTimeErrorAction>,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(machineService.getMachineOperatingTime, action.payload);
    yield* put(MachineDetailsPanelActions.getMachineOperatingTimeSuccess(response));
  } catch (error) {
    yield* put(
      MachineDetailsPanelActions.getMachineOperatingTimeError({
        error,
      })
    );
  }
}

export function* getMachineRemindersSaga(
  action: IGetMachineRemindersRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<RemindersListResponse>
  | PutEffect<IGetMachineRemindersSuccessAction>
  | PutEffect<IGetMachineRemindersErrorAction>,
  void,
  IDependencies
> {
  const { reminderService } = yield* getContext<IDependencies>('dependencies');

  try {
    const { append, ...requestOptions } = action.payload;
    const response = yield* call(reminderService.list, requestOptions);

    const successActionPayload = { ...response, append };

    yield* put(MachineDetailsPanelActions.getMachineRemindersSuccess(successActionPayload));
  } catch (error) {
    yield* put(
      MachineDetailsPanelActions.getMachineRemindersError({
        error,
      })
    );
  }
}

export function* getMachineNotesSaga(
  action: IGetMachineNotesRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<NotesListResponse>
  | PutEffect<IGetMachineNotesSuccessAction>
  | PutEffect<IGetMachineNotesErrorAction>,
  void,
  IDependencies
> {
  const { noteService } = yield* getContext<IDependencies>('dependencies');

  try {
    const { append, ...requestOptions } = action.payload;
    const response = yield* call(noteService.list, requestOptions);

    const successActionPayload = { ...response, append };

    yield* put(MachineDetailsPanelActions.getMachineNotesSuccess(successActionPayload));
  } catch (error) {
    yield* put(
      MachineDetailsPanelActions.getMachineNotesError({
        error,
      })
    );
  }
}

export function* getMachinePositionHistoriesSaga(
  action: IGetMachinePositionHistoriesRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<MachinePositionHistoriesListResponse>
  | PutEffect<IGetMachinePositionHistoriesSuccessAction>
  | PutEffect<IGetMachinePositionHistoriesErrorAction>,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(machineService.listMachinePositionHistories, action.payload);

    yield* put(
      MachineDetailsPanelActions.getMachinePositionHistoriesSuccess(mapMachinePositionHistoriesResponse(response))
    );
  } catch (error) {
    yield* put(
      MachineDetailsPanelActions.getMachinePositionHistoriesError({
        error,
      })
    );
  }
}

export function* getMachinePositionHistoriesMoreDataSaga(
  action: IGetMachinePositionHistoriesMoreDataRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<MachinePositionHistoriesListResponse>
  | PutEffect<IGetMachinePositionHistoriesMoreDataSuccessAction>
  | PutEffect<IGetMachinePositionHistoriesMoreDataErrorAction>,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(machineService.listMachinePositionHistories, action.payload);

    yield* put(
      MachineDetailsPanelActions.getMachinePositionHistoriesMoreDataSuccess(
        mapMachinePositionHistoriesResponse(response)
      )
    );
  } catch (error) {
    yield* put(
      MachineDetailsPanelActions.getMachinePositionHistoriesMoreDataError({
        error,
      })
    );
  }
}

export function* updateMachineSaga(
  action: IUpdateMachineRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<Optional<MachineUpdateResponse>>
  | PutEffect<IUpdateMachineSuccessAction>
  | PutEffect<IUpdateMachineErrorAction>
  | PutEffect<IEditMachineNameModalIsLoadingAction>
  | PutEffect<IEditMachineNameModalIsNotLoadingAction>
  | PutEffect<IHideEditMachineNameModalAction>,
  void,
  IDependencies
> {
  const { id, name } = action.payload;
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    yield* put(MachineModalsActions.editMachineNameModalIsLoading());
    const response = yield* call(machineService.update, { id, name });

    yield* put(MachineDetailsPanelActions.updateMachineSuccess(response));

    yield* put(MachineModalsActions.editMachineNameModalIsNotLoading());
    yield* put(MachineModalsActions.hideEditMachineNameModal());
  } catch (error) {
    console.error(error);

    yield* put(
      MachineDetailsPanelActions.updateMachineError({
        error,
      })
    );

    yield* put(MachineModalsActions.editMachineNameModalIsNotLoading());
    yield* put(MachineModalsActions.hideEditMachineNameModal());
  }
}

export function* updateMachineInfoSaga(
  action: IUpdateMachineInfoRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<Optional<MachineUpdateResponse>>
  | PutEffect<IUpdateMachineInfoSuccessAction>
  | PutEffect<IUpdateMachineInfoErrorAction>
  | PutEffect<IEditMachineInfoModalIsLoadingAction>
  | PutEffect<IEditMachineInfoModalIsNotLoadingAction>
  | PutEffect<IHideEditMachineInfoModalAction>
  | PutEffect<IPollGetMachineListAsReportRequestAction>
  | PutEffect<IGetMachineDetailsRequestAction>,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    yield* put(MachineModalsActions.editMachineInfoModalIsLoading());
    const { siteName, ...updateParams } = action.payload;
    const response = yield* call(machineService.update, updateParams);
    if (updateParams.siteId !== undefined) {
      Object.assign(response?.machineUpdate.data || {}, { site: { name: siteName, id: updateParams.siteId } });
      // TODO: Polling new site info
    }

    yield* put(MachineDetailsPanelActions.updateMachineInfoSuccess(response));
    yield* put(MachineListActions.pollGetMachineListAsReportRequest());
  } catch (error) {
    console.error(error);
    yield* put(MachineDetailsPanelActions.updateMachineInfoError({ error }));
  } finally {
    yield* put(MachineModalsActions.editMachineInfoModalIsNotLoading());
    yield* put(MachineModalsActions.hideEditMachineInfoModal());
  }
}

export function* getMachineNotificationListSaga(
  action: IGetMachineNotificationListRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<NotificationList>
  | PutEffect<IGetMachineNotificationListSuccessAction>
  | PutEffect<IGetMachineNotificationListErrorAction>,
  void,
  IDependencies
> {
  const { notificationService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(notificationService.listShort, action.payload);
    yield* put(MachineDetailsPanelActions.getMachineNotificationListSuccess(response));
  } catch (error) {
    yield* put(
      MachineDetailsPanelActions.getMachineNotificationListError({
        error,
      })
    );
  }
}

export function* getMachineAttachmentsListSaga(
  action: IGetMachineAttachmentsRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<MachineAttachmentsListResponse>
  | PutEffect<IGetMachineAttachmentsSuccessAction>
  | PutEffect<IGetMachineAttachmentsErrorAction>,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(machineService.listAttachments, action.payload);

    yield* put(MachineDetailsPanelActions.getMachineAttachmentsSuccess(response));
  } catch (error) {
    yield* put(
      MachineDetailsPanelActions.getMachineAttachmentsError({
        error,
      })
    );
  }
}

export function* deleteMachineAttachmentSaga(
  action: IDeleteMachineAttachmentRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<Optional<MachineAttachmentDeleteResponse>>
  | PutEffect<IPollMachineAttachmentsRequestAction>
  | PutEffect<IDeleteMachineAttachmentSuccessAction>
  | PutEffect<IDeleteMachineAttachmentErrorAction>,
  void,
  IDependencies
> {
  const { machineId, attachmentId } = action.payload;
  const { machineAttachmentService } = yield* getContext<IDependencies>('dependencies');

  try {
    yield* put(MachineModalsActions.deleteMachineAttachmentModalIsLoading());
    const response: Optional<MachineAttachmentDeleteResponse> = yield* call(machineAttachmentService.delete, {
      machineId,
      attachmentId,
    });

    yield* put(MachineDetailsPanelActions.deleteMachineAttachmentSuccess(response));
    yield* put(
      MachineDetailsPanelActions.pollMachineAttachmentsRequest({
        machineId,
        operation: IPollMachineAttachmentsOperation.DELETE,
      })
    );
  } catch (error) {
    console.error(error);

    yield* put(
      MachineDetailsPanelActions.deleteMachineAttachmentError({
        error,
      })
    );
  } finally {
    yield* put(MachineModalsActions.deleteMachineAttachmentModalIsNotLoading());
    yield* put(MachineModalsActions.hideDeleteMachineAttachmentModal());
  }
}

export function* addMachineAttachmentSaga(
  action: IAddMachineAttachmentRequestAction
): Generator<
  | GetContextEffect
  | CallEffect
  | CallEffect<Optional<MachineAttachmentGetUploadLinkResponse>>
  | PutEffect<IPollMachineAttachmentsRequestAction>
  | PutEffect<IAddMachineAttachmentSuccessAction>
  | PutEffect<IAddMachineAttachmentErrorAction>,
  void,
  IDependencies
> {
  const { machineId, file } = action.payload;
  const { machineAttachmentService } = yield* getContext<IDependencies>('dependencies');
  const currentTime = Date.now();
  try {
    yield* put(MachineModalsActions.addMachineAttachmentModalIsLoading());
    const response = yield* call(machineAttachmentService.getUploadLink, {
      machineId,
      attachmentName: file.name,
      mimeType: file.type || file.originFileObj?.type || '',
    });
    if (!response.attachmentUploadLinkGet.data) return;

    const { fields, url } = response.attachmentUploadLinkGet.data;

    const formData = FormDataBuilder.buildUploadFormFromPresignedUrl({ file: file.originFileObj as File, fields });
    yield* call(machineAttachmentService.uploadByUrl, {
      url,
      formData,
    });

    yield* put(MachineDetailsPanelActions.addMachineAttachmentSuccess());
    yield* put(
      MachineDetailsPanelActions.pollMachineAttachmentsRequest({
        machineId,
        operation: IPollMachineAttachmentsOperation.ADD,
        attachment: {
          name: file.name,
          mimeType: file.type || file.originFileObj?.type || '',
          size: file.size || 0,
          time: currentTime,
        },
      })
    );
  } catch (error) {
    console.error(error);

    yield* put(
      MachineDetailsPanelActions.addMachineAttachmentError({
        error,
      })
    );
  } finally {
    yield* put(MachineModalsActions.addMachineAttachmentModalIsNotLoading());
    yield* put(MachineModalsActions.hideAddMachineAttachmentModal());
  }
}

export function* downloadMachineAttachmentSaga(
  action: IDownloadMachineAttachmentRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<Optional<MachineAttachmentDownloadResponse> | void>
  | PutEffect<IDownloadMachineAttachmentSuccessAction>
  | PutEffect<IDownloadMachineAttachmentErrorAction>,
  void,
  IDependencies
> {
  const { machineId, attachmentId } = action.payload;
  const { machineAttachmentService, toastService, t } = yield* getContext<IDependencies>('dependencies');

  try {
    yield* call(toastService.info, {
      message: t('machineAttachmentList.subHeader') as string,
      description: t('machineAttachmentList.downloadDesc') as string,
    });
    const response = yield* call(machineAttachmentService.download, { machineId, attachmentId });

    const downloadUrl = response?.attachmentDownloadLinkGet.data;
    if (!downloadUrl) return;

    downloadLink({ fileUrl: downloadUrl });

    yield* put(MachineDetailsPanelActions.downloadMachineAttachmentSuccess(response));
  } catch (error) {
    console.error(error);

    yield* put(
      MachineDetailsPanelActions.downloadMachineAttachmentError({
        error,
      })
    );
  }
}

export function* pollMachineAttachmentsListSaga(
  action: IPollMachineAttachmentsRequestAction
): Generator<
  | GetContextEffect
  | SelectEffect
  | SagaGenerator<MachineAttachmentsListResponse, CallEffect<MachineAttachmentsListResponse>>
  | PutEffect<IGetMachineAttachmentsSuccessAction>
  | PutEffect<IMachineAttachmentsIsLoadingAction>
  | PutEffect<IGetMachineAttachmentsErrorAction>,
  void,
  any
> {
  const { machineId, attachment, operation } = action.payload;
  const { machineService } = yield* getContext<IDependencies>('dependencies');
  const previousTotalCount: Optional<number> = yield* select(selectAttachmentsTotalCount);
  const getMachineAttachments = async (): Promise<MachineAttachmentsListResponse> => {
    let predicate: () => boolean;
    const response: MachineAttachmentsListResponse = await machineService.listAttachments({
      machineId,
      paginationOptions: {
        limit: MACHINE_ATTACHMENT_LIST_PAGE_SIZE_LARGE,
      },
      sortOptions: {
        field: MACHINE_ATTACHMENT_LIST_DEFAULT_SORT_FIELD,
        order: SortOrders.Desc,
      },
    });

    const newMachineAttachment = response?.machine.data.attachments.data[0];
    const newTotalCount = response.machine.data.attachments.metadata?.totalCount || 0;

    switch (operation) {
      case IPollMachineAttachmentsOperation.ADD:
        predicate = (): boolean =>
          newMachineAttachment.name === attachment?.name &&
          newMachineAttachment.size === attachment?.size &&
          newMachineAttachment.mimeType === attachment?.mimeType &&
          +new Date(newMachineAttachment.createdAt) > attachment?.time;
        break;
      case IPollMachineAttachmentsOperation.DELETE:
        predicate = (): boolean => newTotalCount !== previousTotalCount;
        break;

      default:
        throw new Error(`Invalid operation: ${operation}`);
    }

    if (!predicate()) {
      throw new Error('Machine Attachments not updated');
    }

    return response;
  };

  try {
    yield* put(MachineDetailsPanelActions.machineAttachmentsIsLoading());
    yield* put(MachineDetailsPanelActions.setMachineAttachmentsActiveSortOrder(SortOrders.Desc));
    const response: MachineAttachmentsListResponse = yield retry(
      POLL_MAX_RETRIES,
      POLL_INTERVAL,
      getMachineAttachments
    );
    yield* put(MachineDetailsPanelActions.getMachineAttachmentsSuccess(response));
  } catch (error) {
    console.error(error);

    yield* put(
      MachineDetailsPanelActions.getMachineAttachmentsError({
        error,
      })
    );
  }
}

export function* machineDetailsPanelSaga(): Generator<ForkEffect<never>, void> {
  yield* takeLatest(MachineDetailsPanelActions.GET_MACHINE_DETAILS_REQUEST, getMachineDetailsSaga);
  yield* takeLatest(MachineDetailsPanelActions.GET_MACHINE_DETAILS_METADATA_REQUEST, getMachineDetailsMetadataSaga);
  yield* takeLatest(MachineDetailsPanelActions.GET_MACHINE_OPERATING_TIME_REQUEST, getMachineOperatingTimeSaga);
  yield* takeLatest(MachineDetailsPanelActions.GET_MACHINE_REMINDERS_REQUEST, getMachineRemindersSaga);
  yield* takeLatest(MachineDetailsPanelActions.GET_MACHINE_NOTES_REQUEST, getMachineNotesSaga);
  yield* takeLatest(MachineDetailsPanelActions.GET_MACHINE_POSITION_HISTORIES_REQUEST, getMachinePositionHistoriesSaga);
  yield* takeLatest(
    MachineDetailsPanelActions.GET_MACHINE_POSITION_HISTORIES_MORE_DATA_REQUEST,
    getMachinePositionHistoriesMoreDataSaga
  );
  yield* takeLatest(MachineDetailsPanelActions.UPDATE_MACHINE_REQUEST, updateMachineSaga);
  yield* takeLatest(MachineDetailsPanelActions.UPDATE_MACHINE_INFO_REQUEST, updateMachineInfoSaga);
  yield* takeLatest(MachineDetailsPanelActions.GET_MACHINE_NOTIFICATION_LIST_REQUEST, getMachineNotificationListSaga);
  yield* takeLatest(MachineDetailsPanelActions.GET_MACHINE_ATTACHMENTS_LIST_REQUEST, getMachineAttachmentsListSaga);
  yield* takeLatest(MachineDetailsPanelActions.DELETE_MACHINE_ATTACHMENT_REQUEST, deleteMachineAttachmentSaga);
  yield* takeLatest(MachineDetailsPanelActions.ADD_MACHINE_ATTACHMENT_REQUEST, addMachineAttachmentSaga);
  yield* takeLatest(MachineDetailsPanelActions.DOWNLOAD_MACHINE_ATTACHMENT_REQUEST, downloadMachineAttachmentSaga);
  yield* takeLatest(MachineDetailsPanelActions.POLL_MACHINE_ATTACHMENTS_REQUEST, pollMachineAttachmentsListSaga);
}
